import { getRefreshToken, setAuthTokens } from './authTokenStorage';
import { refreshAuthWithRefreshToken } from './cognitoAuthApi';
import { getSessionRealm } from './sessionRealm';

import type { AuthResult } from './types';

let inflightRefresh: Promise<AuthResult | null> | null = null;

export type RefreshSessionOptions = {
  /** Boot validation: do not redirect from Cognito; caller terminates with the right realm. */
  skipExpireHandler?: boolean;
};

/**
 * Refreshes Cognito tokens using the stored refresh token.
 * Uses a single in-flight promise so concurrent 401s do not trigger multiple refresh calls.
 */
export async function refreshSessionTokens(
  options?: RefreshSessionOptions
): Promise<AuthResult | null> {
  if (inflightRefresh) {
    return inflightRefresh;
  }

  inflightRefresh = (async () => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      return null;
    }

    const result = await refreshAuthWithRefreshToken(refreshToken, {
      skipExpireHandler: options?.skipExpireHandler === true,
    });
    const realm = getSessionRealm() ?? 'user';
    const resultWithRefreshToken = {
      ...result,
      refreshToken: result.refreshToken ?? refreshToken,
    };
    setAuthTokens(resultWithRefreshToken, realm);
    return resultWithRefreshToken;
  })();

  try {
    return await inflightRefresh;
  } finally {
    inflightRefresh = null;
  }
}
